import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_STACKED = "/logo_dark.png";
const LOGO_LIGHT = "/logo_light.png";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_d04ef3c1-d7de-40d8-b6b3-0d99244d2801/artifacts/0hpd8r65_icon_markremoved%20backround.png";

export default function BillingSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus("error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- poll once per searchParams change; pollPaymentStatus intentionally excluded
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("error");
      return;
    }

    try {
      const response = await axios.get(`${API}/billing/checkout-status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === "paid") {
        setStatus("success");
        setPaymentDetails(data);
      } else if (data.status === "expired") {
        setStatus("error");
      } else {
        // Continue polling
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          pollPaymentStatus(sessionId);
        }, pollInterval);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0E1A] flex items-center justify-center p-6">
      <Card className="w-full max-w-md bg-white dark:bg-[#0D1326] border-white/10">
        <CardHeader className="text-center pt-8">
          <div className="flex items-center justify-center gap-2.5 mb-6">
            <img src={LOGO_ICON} alt="QNNEAL" className="h-12 w-12 object-contain" />
            <span className="font-outfit font-semibold text-xl tracking-tight text-white">QNNEAL</span>
          </div>
          <CardTitle className="font-outfit text-2xl">
            {status === "loading" && "Processing Payment..."}
            {status === "success" && "Payment Successful!"}
            {status === "error" && "Payment Failed"}
          </CardTitle>
        </CardHeader>

        <CardContent className="text-center space-y-6">
          {status === "loading" && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-cyan-400 animate-spin" />
              <p className="text-slate-500 dark:text-slate-400">
                Please wait while we confirm your payment...
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-400" />
              <div className="space-y-2">
                <p className="text-slate-600 dark:text-slate-300">
                  Thank you for upgrading to QNNEAL!
                </p>
                {paymentDetails && (
                  <p className="text-sm text-slate-500">
                    Amount: ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency.toUpperCase()}
                  </p>
                )}
              </div>
              <Button 
                onClick={() => navigate("/dashboard")}
                className="btn-primary w-full"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <XCircle className="w-16 h-16 mx-auto text-red-400" />
              <p className="text-slate-500 dark:text-slate-400">
                We couldn't confirm your payment. Please try again or contact support.
              </p>
              <div className="flex gap-4">
                <Button 
                  onClick={() => navigate("/billing")}
                  variant="outline"
                  className="flex-1 border-white/10"
                  data-testid="try-again-btn"
                >
                  Try Again
                </Button>
                <Button 
                  onClick={() => navigate("/dashboard")}
                  className="flex-1 btn-primary"
                  data-testid="back-to-dashboard-btn"
                >
                  Dashboard
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
